import { type SWRInfiniteKeyedMutator } from 'swr/infinite';

import { type ApiResponseType } from '@libraries/typescript/api_client';

export type OrganizationConversationsResponse = ApiResponseType<
    '/organizations/{organization_id}/conversations',
    'get'
>;

export type ConversationPagesMutator = SWRInfiniteKeyedMutator<
    Array<OrganizationConversationsResponse>
>;

/**
 * Updates a conversation across paginated list data while preserving
 * unresolved values and references for pages that do not change.
 */
export function updateConversationInPages(
    pages: Array<OrganizationConversationsResponse> | undefined,
    conversationId: string,
    update: (
        conversation: OrganizationConversationsResponse['conversations'][number],
    ) => OrganizationConversationsResponse['conversations'][number],
): Array<OrganizationConversationsResponse> | undefined {
    if (!pages) {
        return pages;
    }

    let didChange = false;
    const nextPages = pages.map((page) => {
        let pageChanged = false;
        const nextConversations = page.conversations.map((conversation) => {
            if (conversation.id !== conversationId) {
                return conversation;
            }

            const nextConversation = update(conversation);
            if (nextConversation === conversation) {
                return conversation;
            }

            pageChanged = true;
            didChange = true;
            return nextConversation;
        });

        return pageChanged
            ? {
                  ...page,
                  conversations: nextConversations,
              }
            : page;
    });

    return didChange ? nextPages : pages;
}
